/**
 * ECH block registry — port of the monolith's `__echRegister()` (index.html
 * L8647–8757) plus its `GR[]` write, `ECH_GR_PROPOSED` (L8566–8636) and
 * `ECH_GR_OVERRIDE` (L8560–8565).
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * ECH is Layne's set of real SFMC Content Builder blocks. The whole point is that
 * NO template HTML and NO field list lives in this app: a block's markup is its
 * renderer, and its editable fields are whatever its own `@GUARDRAILS` comment
 * declares. `services/sfmcBlocks.ts` already parsed those comments — but the
 * result was computed and thrown away (`StudioView.tsx:264–277`), so no ECH block
 * could be placed, selected, or governed.
 *
 * TWO KEY SPACES, ONE KEY (repo audit row 13)
 * -------------------------------------------
 * The monolith writes `GR["sfmc_"+id]` and reads it back through
 * `__grMeta(m.type, …)` where `m.type` IS `"sfmc_"+id` — the same key on both
 * sides. The repo wrote `grMap["sfmc_"+id]` and read `grMap[block.type]` where
 * `block.type` was a FULL_REGISTRY key like `hero`, so the two spaces never
 * intersected and every cap, counter, clamp and the submit gate were inert.
 * Registering ECH blocks with `sfmc_<id>` AS their block type restores the
 * monolith's symmetry; `grFor()` below is the single read point.
 *
 * A legacy registry block (`hero`, `cta`, `body`…) still resolves to NO
 * guardrails — that is parity, not an oversight: in the monolith `GR` is
 * populated only from SFMC blocks, so `__grMeta("hero", …)` returns null there
 * too. Do NOT "fix" that by aliasing on `gr.module`: the live blocks declare
 * module names like `header` / `ctaButton` / `mainImage` / **`body`**, and that
 * last one would silently attach ECH_CopyBlock's 325-char rule to the legacy
 * Body Copy block. Verified against the 2026-08-18 block cache.
 *
 * WHAT A CAP APPLIES TO (the 24-character trap)
 * ---------------------------------------------
 * `capOf()` in guardrails.ts returns 0 for a rule with neither `max` nor
 * `lines`, but the monolith's `__grCap` falls back to `(lines||1)*(cpl||24)` —
 * i.e. ANY governed field without a stated limit inherits 24 characters. Handing
 * the raw field map to the enforcement layer truncated 11 image/number fields to
 * 24 characters, which is what broke Main Image and every amenity icon: the URL
 * was clamped mid-string so nothing loaded. So `echLimits()` reproduces the
 * monolith's filter exactly — a field is capped only if it declares `max`, or
 * `lines` AND `cpl`, AND its declared type is not image/url/number/tel.
 */
import { echDropTokenImages } from "./echContact";
import { contactIfColumnHtml, contactLookup } from "./echContactTreatment";
import { hasIfField, ifFieldHtml } from "./echIfField";
import type { FieldDescriptor } from "../types/blocks";
import type { Guardrails, GuardrailField } from "./guardrails";
import { capOf, clamp } from "./guardrails";
import { isShellName, parseGuardrails, stripGuardrails, type SfmcBlock } from "./sfmcBlocks";
import { isEchListName } from "./echLists";
import { FULL_REGISTRY, type AnyBlockDefinition } from "../email/blocks";
import { escML } from "../email/core";
import { AMEN_FALLBACK, AMEN_LIB_BLOCKS, amenFolderLib, amenFolderVersion, amenLib, amenRowsHtml, amenTrimHtml, type AmenIcon } from "./echAmen";
import { dropEmptyImage, unpinFeedPhoto } from "./echImg";
import { fixLegacyCropUrl } from "./bynderCrop";
import { dropEmptyCollectionHeaders, dropEmptyHomeRows, dropEmptyIconBlocks, dropEmptyOptionalLines, rowOf, tidyRowSlashes } from "./echRows";
import { dropEmptyPriceLine, priceLabelValue } from "./echPrice";

/** The palette group every ECH block lands in (monolith `grp:"SFMC Blocks"`). */
export const ECH_GRP = "SFMC Blocks";

/* ── PROPOSED GUARDRAILS (monolith L8547–8636) ──────────────────────────────
   11 of Layne's 28 blocks carry no @GUARDRAILS. She said why: "I wasn't really
   sure what the guardrail should be." These are the obvious ones — image
   dimensions read off the block's own markup, legal copy nobody should edit, a
   division block that is chosen for you. Applied ONLY where a block has none of
   its own, and every one is tagged PROPOSED so it is never mistaken for Layne's
   authored rules. Nothing here is written to SFMC.

   `fields:{}` is what makes the panel render the read-only card instead of an
   editable one; `lock` is the line that card shows. */
const BANNER_LOCK = "This banner can't be edited — the headline is part of the image.";
const BANNER_TAIL = "To change it, replace the asset on the block in Content Builder — every template updates.";
const DISC_LOCKED = "Legal copy is locked and cannot be edited in the Studio.";
export const ECH_GR_PROPOSED: Record<string, Guardrails> = {
  /* ── THE FIVE HERO BANNERS ARE LOCKED (2026-08-18, per Jack) ──────────────
     They were briefly proposed with a Bynder picker + alt field. Wrong: the
     headline ("Find Your Dream Home / New Homes Now Selling", "Coming Soon",
     "Join the VIP List") is TYPESET INTO THE ARTWORK. Letting a division swap
     the photo produces a banner whose words contradict the template it sits in.
     The asset is national creative — it changes in Content Builder, once, and
     every template inherits it. */
  ECH_ComingSoonImage: {
    module: "heroImage", label: "Coming Soon Banner", fields: {}, lock: BANNER_LOCK,
    notes: ["Banner is 600x350 displayed (1200x700 source for retina).",
      "The 'Just Announced / Coming Soon' artwork is baked into the image and cannot be edited as text.",
      BANNER_TAIL], actions: [],
  },
  ECH_JustLaunchedImage: {
    module: "heroImage", label: "Just Launched Banner", fields: {}, lock: BANNER_LOCK,
    notes: ["Banner is 600x350 displayed (1200x700 source for retina).",
      "The 'Now Selling / Sales Just Launched' artwork is baked into the image.",
      BANNER_TAIL], actions: [],
  },
  ECH_AvailableNowImage: {
    module: "heroImage", label: "Available Now Banner", fields: {}, lock: BANNER_LOCK,
    notes: ["Banner is 600x350 displayed (1200x700 source for retina).",
      "The headline artwork is baked into the image and cannot be edited as text.",
      BANNER_TAIL], actions: [],
  },
  ECH_FindDreamHomeImage: {
    module: "heroImage", label: "Find Your Dream Home Banner", fields: {}, lock: BANNER_LOCK,
    notes: ["Banner is 600x350 displayed (1200x700 source for retina).",
      "The headline artwork is baked into the image.",
      BANNER_TAIL], actions: [],
  },
  ECH_VIPImage: {
    module: "heroImage", label: "VIP List Banner", fields: {}, lock: BANNER_LOCK,
    notes: ["Banner is 600x350 displayed (1200x700 source for retina).",
      "The 'Exclusive Opportunity / Join the VIP List' artwork is baked into the image.",
      BANNER_TAIL], actions: [],
  },
  /* ── CONTACT US: A DISPLAY NAME, AND NOTHING ELSE (trimmed 2026-08-20) ────────────
     This entry used to carry a `lock` sentence, three `notes` and an `actions` item —
     all of them OUR prose describing how one of Layne's blocks behaves. Per Jack: that
     belongs in the block's `@GUARDRAILS`, or the dashboard keeps asserting it after she
     changes the block. Two were also simply wrong by the time they were read: the live
     block DOES declare `ContactUrl` now, and the note claiming "this tool does not swap
     it in yet" was overtaken by `echIscPatch`, which swaps in the division's own Contact
     Us layoutblock (verified live — the AZH canvas renders
     `href="https://www.lennar.com/contact?state=AZ&market=PHO"`).

     What is left is a LABEL, which is presentation, not a rule: it turns
     `ECH_ContactUsDefault` into "Contact Us" on the canvas tag and the panel header. The
     block's own `label` wins wherever it declares one, so this only applies to an
     environment where the block carries no guardrails at all.

     ⚠️ Do NOT delete this outright. It was tried, on the reasoning that the live block
     now has its own guardrails so the entry was dead — but the bundled fixture's copy is
     older and has none, so the canvas tag fell back to the raw block name and four tests
     failed. "Inert against live" is not "inert". Inventory: `ech/spec/HARDCODE-FLAGS.md`. */
  ECH_ContactUsDefault: { module: "contactUs", label: "Contact Us", fields: {} },
  ECH_Disclaimer: {
    module: "disclaimer", label: "Disclaimer", fields: {},
    lock: "Legal copy — changes go through Legal.",
    notes: [DISC_LOCKED,
      "Wording changes go through Legal, then Layne updates the block once and every template inherits it."],
    actions: [],
  },
  ECH_DisclaimerMortgage: {
    module: "disclaimer", label: "Disclaimer — Mortgage", fields: {},
    lock: "Legal copy — use this version when Lennar Mortgage is mentioned.",
    notes: [DISC_LOCKED, "Use this variant only when Lennar Mortgage is mentioned in the email."],
    actions: [],
  },
  ECH_DisclaimerMortgageTitle: {
    module: "disclaimer", label: "Disclaimer — Mortgage + Title", fields: {},
    lock: "Legal copy — use this version when Mortgage and Title are both mentioned.",
    notes: [DISC_LOCKED, "Use this variant when both Lennar Mortgage and Lennar Title are mentioned."],
    actions: [],
  },
  ECH_DisclaimerMortgageTitleLIA: {
    module: "disclaimer", label: "Disclaimer — Mortgage + Title + LIA", fields: {},
    lock: "Legal copy — use this version when Mortgage, Title and Insurance are all mentioned.",
    notes: [DISC_LOCKED, "Use this variant when Lennar Mortgage, Lennar Title and Lennar Insurance are all mentioned."],
    actions: [],
  },
  ECH_DividerLine: {
    module: "divider", label: "Divider Line", fields: {},
    lock: "A fixed spacing rule — nothing to edit.",
    notes: ["A fixed spacing rule. Colour, thickness and margins are locked.",
      "Nothing to edit — remove the block if the divider is not wanted."],
    actions: [],
  },
};

/* PROPOSED FIELD OVERRIDES (monolith L8555–8565) — a narrower rule on a block
   that ALREADY has authored guardrails. ECH_GR_PROPOSED only fills blocks with
   none, so it can't tighten an existing cap; this merges per field on top of
   Layne's rule and is tagged PROPOSED the same way. */
export const ECH_GR_OVERRIDE: Record<string, Record<string, Partial<GuardrailField>>> = {
  /* 100 was the authored cap. A community name that long doesn't exist, and the
     header renders on ONE line beside the phone number — past ~50 it wraps and
     breaks the lockup. (2026-08-18, per Jack.) */
  ECH_Logo: { community: { max: 50 } },
};

/** Everything the app knows about one registered ECH block. */
export interface EchMeta {
  /** the block type, i.e. `sfmc_<contentId>` — this IS the FULL_REGISTRY key */
  key: string;
  /** the SFMC block name, e.g. `ECH_Logo` — the ECH_DYN / ECH_CHOICES map key */
  echName: string;
  sfmc: { id: string; name: string };
  /** the block's own rule set, merged with any PROPOSED fill/override */
  full: Guardrails | null;
  /** ONLY the fields a cap may be enforced against (monolith `_lim`) */
  gr: Guardrails;
  /** true when the block declares no editable field at all */
  locked: boolean;
  /** the one grey sentence a locked block's card shows */
  lockNote: string;
  /** the rules came from ECH_GR_PROPOSED / _OVERRIDE, not from Content Builder */
  proposedGr: boolean;
  /** the panel's field list, derived from the DECLARED types (audit row 1) */
  fields: FieldDescriptor[];
  /** the declared field map, in declaration order — the token substitution set */
  declared: Record<string, GuardrailField>;
}

const ECH: Record<string, EchMeta> = {};
/** bumped on every successful registration so React can re-derive the palette */
let VERSION = 0;

/** monolith `_lim` — the ONLY fields a cap may be enforced against. */
export function echLimits(fields: Record<string, GuardrailField>): Record<string, GuardrailField> {
  const lim: Record<string, GuardrailField> = {};
  for (const [k, fr] of Object.entries(fields || {})) {
    const declares = fr.max != null || (fr.lines != null && fr.cpl != null);
    /* `select` joins the exempt list (2026-08-20): its value comes from the block's
       own option list, so there is no free typing to cap and a character counter on
       a font-size picker is noise. Same reasoning the monolith used to exempt
       image/url/number/tel. */
    const textish = !/^(image|url|number|tel|select)$/i.test(String(fr.type || ""));
    if (declares && textish) lim[k] = fr;
  }
  return lim;
}

/**
 * monolith L8741–8754 — HONOUR THE DECLARED TYPE.
 * Every field used to collapse to a text box, so an `image` field got a bare URL
 * input instead of the Bynder picker the Studio already has, and url/number/tel
 * lost their input treatment.
 */
export function echFields(fields: Record<string, GuardrailField>): FieldDescriptor[] {
  const out = Object.keys(fields || {}).map((k) => {
    const f = fields[k] || {};
    const t = String(f.type || "").toLowerCase();
    const fd: FieldDescriptor = { k, label: f.label || k, type: "text" };
    if (t === "image") fd.type = "image";
    else if (t === "url") fd.type = "url";
    /* A DECLARED `select` IS A PICKER, NOT A TEXT BOX (2026-08-20 — Jack: "confused
       why is the headline size… how did this get pulled in/created?").
       `ECH_CopyBlock.HeadlineSize` is `{type:"select", options:[16,18]}`, the headline
       font size in px. `select` was not handled here, so it fell through to the plain
       text default and rendered as a free-text field — which is how "dfdfd" ended up
       in it. That value substitutes into the block's own `font-size:{{HeadlineSize}}`,
       so a typo does not just look wrong, it ships broken CSS in the email.
       `seg` is the repo's existing N-option control (`Seg2`, Inspector.tsx:308) and
       renders one button per option, so the block's own list IS the whole UI — no
       list is maintained here, exactly as with the amenity library and the CTA
       dropdown. Checked BEFORE the `lines` branch: a select with a line budget is
       still a select. */
    else if (t === "select" && f.options && f.options.length) {
      fd.type = "seg";
      fd.opts = f.options.map((o) => [String(o), String(o)] as const);
    }
    else if (f.lines && f.lines > 1) fd.type = "textarea";
    else if (t === "number" || t === "tel") fd.type = "text";
    if (f.width) fd.w = f.width;
    if (f.height) fd.h = f.height;
    return fd;
  });
  /* ── DECLARE THE STEPPED PICKER ON THE BLOCKS THAT HAVE SOMETHING TO PICK
     (2026-08-20 — the first of the three faults behind Jack's "dynamic is not set
     up" on the Featured Plan block) ────────────────────────────────────────────
     `Inspector.tsx:964` finds the Dynamic pane with
     `def.fields.find(f => f.type === "livefill")`, and exactly TWO blocks in the
     codebase declared that type: the legacy featured module (registry-extended.ts
     :375, mode "plans") and the legacy listing table (:417, mode "rows"). No ECH
     block declared one, so `DynLiveFill` — the stepped Communities → Floorplans →
     Homesites wizard, built and tested — could never render on
     ECH_Featured1CTA / ECH_Featured2CTA / ECH_ListingTable. The component existed;
     nothing routed to it.
     Content Builder will never emit a `livefill` field, because the picker is OUR
     control and not one of Layne's tokens — so it is synthesised here, where the
     panel's field list is built from the block's own guardrails. */
  const mode = echPickMode(fields);
  if (mode) out.push({ k: ECH_PICK_FIELD, label: mode === "rows" ? "Live homes from SFMC" : "Live floorplans from SFMC", type: "livefill", mode });
  return out;
}

/**
 * The synthetic field's key. Deliberately NOT a name Content Builder could emit,
 * so it can never collide with one of Layne's declared tokens, and it is never put
 * in the block's `defaults` (those are built from `Object.keys(declared)`, which
 * this is not in). Nothing reads it — `Inspector` uses the field's `mode` — but a
 * key is required by FieldDescriptor and an anonymous one would be a React key
 * collision waiting to happen.
 */
export const ECH_PICK_FIELD = "__echPick";

/**
 * WHICH PICKER, IF ANY, THIS BLOCK SHOULD OFFER — read off the block's OWN declared
 * fields.
 *
 * NO DEAD CONTROLS. This is the same rule that removed the greyed-out "Dynamic"
 * button and that keeps the Manual/Dynamic strip off ECH_SocialIcons and
 * ECH_MainImage: a control is only drawn where it can change something. A block
 * that declares no plan and no home fields has nothing for a pick to land on, so
 * it gets no picker — not a picker that silently does nothing.
 *
 * `SqFtLabel` / `BedsLabel` do NOT count. They are wording, they stay manual in
 * `ECH_DYN`, and a block carrying only those has nothing to fill.
 *
 * WHY THE KEY NAMES ARE SPELLED HERE RATHER THAN DERIVED FROM `ECH_DYN`. `echDyn`
 * imports `echNameOf` / `echMeta` / `echAmenLib` from THIS module, so importing
 * `ECH_DYN` back would close a cycle — and `registerOne` runs at module-init time
 * for the bundled fixture, which is exactly when a cyclic import reads
 * `undefined`. The pairing to keep in step is: `PlanName` here ↔ `ECH_DYN`'s
 * `plan.self.*` entries (echDyn.ts:414), `home1Homesite` here ↔ its generated
 * `home.<n>.*` map (echDyn.ts:~150).
 */
export function echPickMode(fields: Record<string, GuardrailField>): "" | "plans" | "rows" {
  const f = fields || {};
  /* the listing table is checked first: it is the only one of the two that could
     ever plausibly also carry a PlanName, and its rows are the bigger pick */
  if (f.home1Homesite || f.home1Plan) return "rows";
  if (f.PlanName) return "plans";
  return "";
}

/** Merge ECH_GR_OVERRIDE over an authored field map. Copies first — the source
 *  points into the live pack, and mutating it would rewrite the block everywhere
 *  else that reads it (monolith L8657–8676). */
function applyOverride(name: string, fields: Record<string, GuardrailField>): { fields: Record<string, GuardrailField>; touched: boolean } {
  const ov = ECH_GR_OVERRIDE[name];
  if (!ov) return { fields, touched: false };
  const merged: Record<string, GuardrailField> = {};
  for (const fk of Object.keys(fields)) {
    const base = fields[fk] || {};
    if (!ov[fk]) { merged[fk] = base; continue; }
    const cp: GuardrailField = { ...base, ...ov[fk] };
    /* a tightened `max` has to kill any lines×cpl budget, or capOf picks the
       WIDER of the two and the override does nothing */
    if (ov[fk].max != null) { delete cp.lines; delete cp.cpl; }
    merged[fk] = cp;
  }
  return { fields: merged, touched: true };
}

/**
 * Register one ECH block. Mutates FULL_REGISTRY under `sfmc_<id>`, exactly as the
 * monolith writes `M[key]` — so every existing `FULL_REGISTRY[b.type]` call site
 * (canvas render, inspector header, toasts, document assembly) keeps working
 * with no change.
 */
/**
 * Enforce Layne's own segment convention at render time (2026-08-21).
 *
 * Her blocks mark optional runs of copy with paired comments —
 * `<!-- SEGMENT n: … -->body<!-- END SEGMENT n -->` — and the body of every
 * segment after the first begins with the literal ` | ` separator. Her comments
 * instruct the human editor to delete an emptied segment AND its leading pipe;
 * this does exactly that to the RENDERED output, so a Header whose community
 * field is empty (every market send, after the community.name fix) reads
 * "Phoenix, AZ | 480-345-0077" and not "| Phoenix, AZ | 480-345-0077".
 *
 * Empty = the body's text with tags, entities, pipes and whitespace stripped is
 * "" — so a phone segment whose `tel:` anchor rendered no digits counts as empty
 * even though its markup is not. Markers are kept on survivors (they are Layne's
 * editing instructions and cost nothing); an emptied segment goes entirely.
 * Blocks without segment markers pass through byte-identical.
 */
export function dropEmptySegments(html: string): string {
  if (!/<!--\s*SEGMENT\b/i.test(html)) return html;
  let first = true;
  let kept = 0;
  let matched = 0;
  let out = html.replace(
    /(<!--\s*SEGMENT\b[\s\S]*?-->)([\s\S]*?)(<!--\s*END SEGMENT\b[\s\S]*?-->)/gi,
    (_m, open: string, body: string, close: string) => {
      matched += 1;
      const text = body.replace(/<[^>]*>/g, " ").replace(/&nbsp;|&#160;/gi, " ").replace(/[|\s]+/g, "");
      if (!text) return "";
      const b = body.replace(/^\s*\|\s*/, first ? "" : " | ");
      first = false;
      kept += 1;
      return open + b + close;
    },
  );
  /* ── ALL SEGMENTS GONE MEANS THE LINE'S OWN SPACE GOES TOO (2026-08-25) ──────────
     A division, via Feedback: "we do have divisions and emails that remove all info
     from the header. In those cases, the space for that copy needs to be removed."
     Dropping the segments left the EMPTY container behind — plus the 10px spacer above
     it — so a header with no community, city or phone still reserved that gap.

     STRUCTURAL, NOT COMMENT-MATCHED (corrected the same day, from an audit). The first
     attempt anchored on Layne's instruction text "delete this entire div if all three
     segments are removed" — which is what her editing note MEANS but not what the live
     block SAYS; the real markup carries a bare `<!-- Copy -->`. So it matched nothing and
     the reported bug was still there while two hand-built tests passed. This walks the
     markup instead: find the element that WRAPPED the segments, and if it now holds no
     text, remove it and an immediately preceding spacer div. Nothing depends on comment
     wording, so an edit in Content Builder cannot silently switch it off. */
  if (matched > 0 && kept === 0) out = dropEmptyWrapper(out, html);
  return out;
}

/**
 * Remove the element that held the segments once every one of them is gone, and the
 * spacer immediately above it. Called only when at least one segment MATCHED and none
 * survived — the `matched` half matters, because a block whose END marker is misspelled
 * would otherwise look like "all segments empty" and lose real copy.
 *
 * `before` is the pre-drop markup, used only to locate the wrapper: the segment markers
 * are still in it, so its own open tag is the nearest one to their left.
 */
function dropEmptyWrapper(after: string, before: string): string {
  const at = before.search(/<!--\s*SEGMENT\b/i);
  if (at < 0) return after;
  /* the nearest opening tag to the LEFT of the first segment marker is the wrapper */
  const opens = [...before.slice(0, at).matchAll(/<(div|p|span|td)\b[^>]*>/gi)];
  const open = opens[opens.length - 1];
  if (!open) return after;
  const tag = String(open[1]).toLowerCase();
  const openTag = String(open[0]);
  const i = after.indexOf(openTag);
  if (i < 0) return after;
  /* walk to the MATCHING close, counting nesting — a first-`</div>` stop would leave a
     stray closing tag behind the moment Layne nests anything in there */
  const re = new RegExp("<" + tag + "\\b[^>]*>|</" + tag + "\\s*>", "gi");
  re.lastIndex = i;
  let depth = 0, end = -1, mm: RegExpExecArray | null;
  while ((mm = re.exec(after))) {
    if (mm[0][1] === "/") { depth -= 1; if (depth === 0) { end = mm.index + mm[0].length; break; } }
    else depth += 1;
  }
  if (end < 0) return after;
  const inner = after.slice(i + openTag.length, end - (tag.length + 3));
  const text = inner.replace(/<[^>]*>/g, " ").replace(/&nbsp;|&#160;/gi, " ").replace(/[|\s]+/g, "");
  if (text) return after;                       /* something real is still in there */
  let start = i;
  /* ── AND THE SPACER ABOVE IT ────────────────────────────────────────────────────
     Only when nothing but whitespace and COMMENTS sit between the two — Layne's markup
     has a bare `<!-- Copy -->` label there, which a `\s*$` anchor missed on the first
     try (measured against the fixture: the container went, the 10px gap stayed). Those
     labels are hers, so they come out with the line they annotate. */
  const head = after.slice(0, i);
  const GAP = "(?:\\s|<!--(?:(?!-->)[\\s\\S])*?-->)*";
  const sp = new RegExp("<!--\\s*Spacer\\s*-->" + GAP + "<(div|p)\\b[^>]*>(?:(?!<\\/?\\1\\b)[\\s\\S])*?<\\/\\1\\s*>" + GAP + "$", "i").exec(head);
  if (sp) start = sp.index;
  else {
    /* no spacer: still take a trailing comment label rather than leaving it orphaned */
    const lab = new RegExp(GAP + "$", "i").exec(head);
    if (lab && lab[0]) start = lab.index;
  }
  return after.slice(0, start) + after.slice(end);
}

function registerOne(block: SfmcBlock): EchMeta | null {
  const name = String(block.name || "");
  if (!block.id) return null;
  const key = "sfmc_" + block.id;

  let g: Guardrails | null = block.guardrails ?? parseGuardrails(block.html || "");
  /* fall back to a PROPOSED rule set only when the block declares none of its own */
  let proposed = false;
  if ((!g || !Object.keys(g).length) && ECH_GR_PROPOSED[name]) { g = ECH_GR_PROPOSED[name]; proposed = true; }

  let declared: Record<string, GuardrailField> = (g && g.fields) || {};
  const ov = applyOverride(name, declared);
  declared = ov.fields;
  if (ov.touched) proposed = true;

  const fkeys = Object.keys(declared);
  const limits = echLimits(declared);
  const html = stripGuardrails(String(block.html || ""));

  /* the block's OWN markup + one starting value per declared field */
  const defaults: Record<string, unknown> = { __html: html };
  fkeys.forEach((k) => { defaults[k] = declared[k].default != null ? declared[k].default : (declared[k].label || ""); });

  const meta: EchMeta = {
    key, echName: name, sfmc: { id: String(block.id), name },
    /* the merged map, not the authored one — `g.fields` still points at what
       Content Builder sent, and anything reading it after an ECH_GR_OVERRIDE
       (or a PROPOSED fill) would silently see the pre-override rule */
    full: g ? { ...g, fields: declared } : null,
    gr: { module: (g && g.module) || name, label: (g && g.label) || name, source: (g && g.source) || (proposed ? "PROPOSED — not yet in SFMC" : ""), notes: (g && g.notes) || [], actions: (g && g.actions) || [], image: (g && g.image) || null, fields: limits, sub: (g && g.sub) || undefined },
    locked: !fkeys.length,
    lockNote: (g && g.lock) || "",
    proposedGr: proposed,
    fields: echFields(declared),
    declared,
  };

  const def: AnyBlockDefinition = {
    label: (g && g.label) || name,
    kind: "module",
    grp: ECH_GRP,
    /**
     * ONE DELIBERATE DEVIATION FROM THE MONOLITH, and the reason for it.
     * The monolith registers ECH blocks `hidden:true` because its templates are
     * assembled by `echDoc(curTpl)` — the palette is not the way in. This repo
     * has no `echDoc()` yet (that is the template-assembly layer, not in this
     * pass), so a hidden ECH block would be unreachable: unplaceable and
     * unselectable, which is exactly the defect audit row 14 records. Visible is
     * what makes the block real here.
     */
    hidden: false,
    def: () => ({ ...defaults }),
    render: (d: Record<string, unknown>) => {
      let out = String(d.__html || "");
      /* ── AN UNCUT IFCOLUMN REGION IS DECIDED FROM THE BLOCK'S OWN VALUES (2026-09-23,
         Lauren Gleim, Form Feedback 154) ────────────────────────────────────────────
         "The second button in 'We're Here to Help You' displays {{Cta2Label}}." The
         region that holds that button (`<!--IFCOLUMN:Cta2_Url-->`) is cut by
         `echContactPatch`, and only once the division's Contact Us row has answered.
         Before that (no division picked yet, the row still loading, or the lookup
         failed) the pristine markup reached this render with the region intact, and
         the second button printed its own token. A region still marked here has not
         been cut, so it is decided from what the block itself holds: kept when a value
         answers its column, deleted otherwise. Once the row answers, the patch writes
         markup with no markers left and this is a no-op, so NEV's second button comes
         back the moment its row loads. */
      if (out.indexOf("IFCOLUMN:") > -1) out = contactIfColumnHtml(out, contactLookup(d, null, declared));
      /* ── IFFIELD: A REGION SHOWN ONLY WHEN ITS YES/NO FIELD SAYS YES (2026-10-29, Layne) ──
         The Next Gen logo row on the Featured cards sits inside
         <!--IFFIELD:ShowNextGen--> … <!--/IFFIELD:ShowNextGen-->; see echIfField.ts. */
      if (hasIfField(out)) out = ifFieldHtml(out, d);
      /* ── AN OPTIONAL LINE IS DECIDED BEFORE ITS TOKEN DISAPPEARS (2026-08-23) ──
         `dropEmptyOptionalLines` reads the TOKENS inside the marker pair to know
         which fields the line depends on, so it has to run on the raw markup — after
         the substitution loop below there is nothing left to read and the line
         always looked "static", which is why the first version of this silently
         kept ECH_GOInvite's empty masterplan div. */
      out = dropEmptyOptionalLines(out, d);
      /* a Featured card with no price ships no "From the" line (echPrice, Justin 2026-10-26) */
      out = dropEmptyPriceLine(out, d, declared as Record<string, { token?: string } | undefined>);
      /* ── A HERO WITH COPY UNDERNEATH DRAWS TALLER (2026-10-10, Jack: "if there's text underneath
         it can be a bit taller") ───────────────────────────────────────────────────────────
         The overlay bake keeps the photo at the block's full shape and adds the copy band below,
         so the image is taller than the Height dropdown. `ovDrawH` (services/overlaySession.ts) is
         that drawn height; it replaces the height token only while the image field still holds
         that bake. The dropdown value itself is untouched (crop tool, Airtable field). */
      const imgKey = fkeys.find((k) => /^image$/i.test(String((declared[k] || {}).type || "")));
      const hKey = fkeys.find((k) => /^(img|image|main|hero)?_?height$/i.test(k));
      const drawH = imgKey && hKey && /^\d+$/.test(String(d.ovDrawH || "")) && /^data:image\/|\/\/intel\.lennar-hub\.com\/hero-img\/|\/\/image\.[a-z0-9.]*(exct\.net|lennar\.com)\//i.test(String(d[imgKey] || "")) && !/bynder\.com/i.test(String(d[imgKey] || ""))
        ? String(d.ovDrawH) : "";
      fkeys.forEach((k) => {
        const tok = declared[k].token || "{{" + k + "}}";
        /* last line of defence — however the value arrived (paste, dynamic fill,
           import) the RENDERED email obeys the guardrail */
        let v = clamp(String(d[k] == null ? "" : d[k]), limits[k]);
        if (drawH && k === hKey) v = drawH;
        /* a price prefix never prints without a price behind it (echPrice) */
        if (k === "PriceLabel") v = priceLabelValue(v, d, declared as unknown as Record<string, unknown>);
        /* a photo saved with the 09-15 crop shape ships the region its requester chose (bynderCrop, 2026-09-18) */
        if (/^image$/i.test(String((declared[k] || {}).type || ""))) v = fixLegacyCropUrl(v);
        out = out.split(tok).join(escML(v));
      });
      /* EMPTY LISTING ROWS ARE REMOVED, NOT LEFT BLANK (monolith L8720–8735).
         The block ships 10 fixed home rows and its own guardrail says unused rows
         "must be deleted entirely, not left blank" — otherwise the email renders
         blank striped rows. Rows are delimited by <!-- HOME n --> markers, so a
         row whose homesite AND plan are both empty is dropped here.
         Ported ahead of the rest of audit row 22 because making the block
         selectable without it would ship 10 blank striped rows the moment anyone
         places the Listing Table. The FUNCTION now lives in `echRows` alongside
         the panel's per-row × so the two cannot drift on what "empty" means —
         both test Homesite OR Plan, and only that. */
      /* ── A TOKEN THE BLOCK DID NOT DECLARE, BUT THE DATA HOLDS (2026-08-22) ────
         The listing fill re-cuts the table into one section per community, so a
         three-community request renders `{{collection3Name}}` — a token Layne's
         block never declared and the loop above therefore never substitutes. It
         would ship as literal braces in a live send. Anything the block's own field
         list covers has already been replaced (and clamped); this only fills a token
         the DATA answers, and leaves a genuinely unknown one alone so a markup typo
         stays visible rather than silently blanking. */
      out = out.replace(/\{\{([A-Za-z0-9_]+)\}\}/g, (whole, k: string) =>
        Object.prototype.hasOwnProperty.call(d, k) ? escML(String(d[k] == null ? "" : d[k])) : whole);
      /* an image whose src is STILL a token is a broken image that widens the table —
         it goes (echContact.echDropTokenImages); text tokens stay visible as above */
      out = echDropTokenImages(out);
      /* a Featured card's feed photo keeps its own shape instead of being pulled to
         3:2 — the img's pinned height goes, its cell holds the 180 (echImg, 2026-09-18) */
      out = unpinFeedPhoto(out, d, declared);
      out = dropEmptyHomeRows(out, d);
      /* a row missing its homesite number or a bed count keeps its slashes between values only (echRows, 2026-09-18) */
      if (fkeys.some((k) => rowOf(k))) out = tidyRowSlashes(out);
      /* an EMPTY collection header takes its row with it — the single-community
         fill blanks the second header deliberately (echRows, 2026-08-21) */
      out = dropEmptyCollectionHeaders(out, d);
      /* EMPTY SEGMENTS GO, PIPE AND ALL (2026-08-21). Layne authors removable
         segments — `<!-- SEGMENT 2: CITY, STATE — delete to remove. If segment 1
         was removed, also delete the leading pipe below -->` — with the pipe
         handling written out as a MANUAL instruction to whoever edits the block in
         Content Builder. The Studio is that editor here, so it follows her own
         instruction mechanically: a segment whose rendered text is empty is
         removed, and the first surviving segment carries no leading pipe. Without
         this, a market send (community="") rendered "| Phoenix, AZ | 480-…". */
      out = dropEmptySegments(out);
      /* the two note-derived removals swept out of the guardrails on 2026-08-23 —
         an unused social icon, and an optional line (masterplan) with nothing in it */
      out = dropEmptyIconBlocks(out, d);
      /* AMENITY BLOCKS: AN EMPTY SLOT WOULD RENDER A BROKEN IMAGE, so the cell goes
         and the rest re-even (monolith L8736–8738, audit row 17). Gated on the block
         DECLARING icon pairs, exactly as the monolith gates it — no other block's
         markup is parsed.
         ── AND THE SAME IS TRUE OF A SINGLE IMAGE (2026-08-20) ──────────────────
         `icon<N>` is the GRID case. The Featured Plan blocks declare one `PlanImage`
         fed from `plan.self.image`, which resolves to "" for a community whose SFMC
         pack has no plans — so those blocks shipped `<img src="">`, a broken image in
         a live send, with this guard sitting one `if` away and never reached. The two
         are mutually exclusive on purpose: both look for `img[src=""]`, and they give
         DIFFERENT answers (a grid re-evens its survivors; a single image takes its
         column with it), so a block must go through exactly one. */
      try {
        /* THE BLOCK'S OWN ROW SPLIT FIRST (2026-08-27). `amenRowsHtml` handles the merged
           1-8 icon block, where the icons are re-dealt across rows per her declared
           `rowSplit` — 5 icons is 3+2, not the 4+1 a delete-in-place trim produces. It
           returns the html untouched for any block that is not that grid, so the older
           3/4-icon blocks still take `amenTrimHtml` exactly as before. */
        if (fkeys.some((k) => /^icon\d+$/.test(k))) {
          const before = out;
          out = amenRowsHtml(out, meta.full as unknown as Parameters<typeof amenRowsHtml>[1]);
          if (out === before) out = amenTrimHtml(out);
        }
        else if (fkeys.some((k) => /^image$/i.test(String((declared[k] || {}).type || "")))) out = dropEmptyImage(out);
      } catch { /* the untrimmed grid is better than no canvas */ }
      return '<tr><td style="padding:0">' + out + "</td></tr>";
    },
    fields: meta.fields,
  };

  FULL_REGISTRY[key] = def;
  ECH[key] = meta;
  return meta;
}

/**
 * Port of `__echRegister(pack)` — registers ALL of Layne's blocks, not just the
 * ones carrying guardrails. `buildRegistry()` skips an un-annotated block (11 of
 * her 28 have none yet), which left holes in every assembled template: guardrails
 * give editable fields when present, and the block still renders truthfully when
 * they don't.
 *
 * Returns how many registered.
 */
/* ── GOVERNED BLOCKS ADMITTED BY ID, NOT BY PREFIX (2026-08-21) ────────────────
   Jack: "i believe it's an existing block in sfmc as well, just not part of the
   template order yet" — and he was right. "07 - Listing Table" (1027211) carries
   authored guardrails (module listingTable: groupName + viewUrl editable, rows
   are homesite data) but no ECH_ name, so the prefix filter dropped it. Admitted
   here so the Featured section's layout toggle can swap to it. Keep this list
   short and deliberate — it is the exception, the ECH_ prefix stays the rule. */
/* 1038758 = Layne's SecondaryImages240 (2026-10-16): loads whether or not it carries the
   ECH_ prefix, so its pair reaches Secondary Image 1/2 (Katy, 2026-10-19) */
export const ECH_EXTRA_IDS: ReadonlySet<number> = new Set([1027211, 1038758]);

export function registerEchBlocks(blocks: SfmcBlock[]): number {
  let n = 0;
  for (const b of blocks || []) {
    const name = String(b.name || "");
    if (!/^ECH_/i.test(name) && !ECH_EXTRA_IDS.has(Number(b.id))) continue;
    /* a definition block, not a module (monolith __grSync L13128) — ECH_TPL_*
       carries `kind:"template"` and a `blocks:[…]` sequence, and would otherwise
       appear in the palette as 7 empty locked blocks */
    if (/^ECH_TPL_/i.test(name)) continue;
    /* likewise `ECH_LIST_*` — a controlled vocabulary (`kind:"list"`), not a module.
       Excluded BY PREFIX rather than by name so the next `ECH_LIST_` block needs no
       code change here, which is the same argument that makes `optionsFrom` a block
       name rather than an inline list. Loaded by `echLoadLists`. */
    if (isEchListName(name)) continue;
    /* a document shell (`ECH_V2_Shell`, 2026-10-06) is what blocks render INSIDE, not
       a block — a template names it, the palette must never offer it */
    if (isShellName(name)) continue;
    if (registerOne(b)) n++;
  }
  if (n) VERSION++;
  return n;
}

/* ── A SAVED DOCUMENT FINDS ITS BLOCKS BY NAME WHEN THE ASSET ID MOVED (2026-10-12) ──────
   A block's type is `sfmc_<asset id>`. When Layne replaces a block in Content Builder (the
   `_TEST` copies on 10/12 all got new ids), every document saved before that points at ids
   the library no longer has, and a proof version opened BLANK — every block a bare "Marketing
   Cloud block" with no markup (found live in the M2 Discovery demo, job 1045700). The saved
   block still carries its name (`__ech.name`), so an unknown type is re-pointed at the block
   that has that name today. Known types are left exactly as they are. */
export function rebindEchTypes<T extends { type: string; __ech?: { name?: string; id?: string } }>(blocks: T[]): { blocks: T[]; changed: number } {
  let changed = 0;
  const out = (blocks || []).map((b) => {
    if (!b || ECH[b.type] || !/^sfmc_/.test(String(b.type))) return b;
    const nm = b.__ech && b.__ech.name;
    const m = nm ? echByName(nm) : null;
    if (!m) return b;
    changed++;
    return { ...b, type: m.key, __ech: { ...b.__ech, id: String(m.sfmc.id) } };
  });
  return { blocks: out, changed };
}

/** true when a block type is an ECH block (`sfmc_<id>`), not a legacy registry key */
export function isEch(type: string): boolean { return !!ECH[type]; }

/** everything known about a registered ECH block, or null for a legacy type */
export function echMeta(type: string): EchMeta | null { return ECH[type] || null; }

/** the ECH block name (`ECH_Logo`) for a block type, or "" for a legacy type */
export function echNameOf(type: string): string { return (ECH[type] && ECH[type].echName) || ""; }

/** every registered ECH block's meta — for rules that scan the library (`placement.bracketedBy`, 2026-09-17) */
export function echBlocksMeta(): EchMeta[] { return Object.keys(ECH).map((k) => ECH[k]); }

/**
 * Look a registered block up by its SFMC NAME (`ECH_Logo`) rather than its type.
 *
 * A template definition block references its blocks by name; the ContentID it
 * resolves to has to come from the block we actually pulled, not from a table in
 * this repo. This is the repo's stand-in for the monolith's
 * `window.__ECH_BLOCK_HTML[name].id` read inside `echSeq` (L8832).
 */
export function echByName(name: string): EchMeta | null {
  const want = String(name || "").toLowerCase();
  if (!want) return null;
  for (const k of Object.keys(ECH)) if (ECH[k].echName.toLowerCase() === want) return ECH[k];
  return null;
}

/**
 * THE single guardrail read point (monolith `__grFor(t)` → `GR[t]`).
 *
 * Symmetric with the write: an ECH block's type IS its `GR` key. A legacy type
 * resolves to undefined, which is what the monolith does too — see the header.
 */
export function grFor(type: string): Guardrails | undefined { return ECH[type] ? ECH[type].gr : undefined; }

/** one field's rule, or null when the field is not capped (monolith `__grMeta`) */
export function grMeta(type: string, field: string): GuardrailField | null {
  const g = grFor(type);
  return (g && g.fields && g.fields[field]) || null;
}

/** clamp a value to its field's cap (monolith `__grClampVal`) */
export function grClampVal(type: string, field: string, value: unknown): string {
  const r = grMeta(type, field);
  const s = String(value == null ? "" : value);
  if (!r || !capOf(r)) return s;
  return clamp(s, r);
}

/**
 * The amenity icon library, off the amenity blocks' OWN declared defaults (audit
 * row 17). This is the seam the monolith fills with `window.__ECH_LIVE` — the
 * registry is where the declared maps live here, so the lookup is here and the
 * logic stays pure in `echAmen.ts`.
 *
 * Cached on the registration VERSION, not forever: the monolith caches on the
 * first non-empty read (`window.__amenLibCache`), which in this repo would freeze
 * the library from whichever pull happened to land first. A version key gives the
 * same "don't rebuild on every render" benefit and still picks up a fresher pull.
 */
let LIB: { v: number; fv: number; lib: AmenIcon[] } | null = null;
export function echAmenLib(): AmenIcon[] {
  const fv = amenFolderVersion();
  if (LIB && LIB.v === VERSION && LIB.fv === fv) return LIB.lib;
  /* PREFERENCE ORDER, widest source first (2026-08-21):
       1. the SFMC folder — Layne's "Amenity Icons", 45 icons, the real inventory;
       2. the amenity blocks' declared icon defaults — how this used to work;
       3. the hardcoded four.
     The folder wins because it is the only source Layne can add to without a code
     change, which is the entire reason for reading it. 2 and 3 stay as the path
     for a worker that is unreachable or a folder that is empty — the picker must
     never render zero icons, because that degrades it to raw photo cards. */
  let lib = amenFolderLib();
  if (!lib.length) lib = amenLib(AMEN_LIB_BLOCKS.map((n) => echByName(n)?.declared || null));
  if (!lib.length) lib = AMEN_FALLBACK;
  if (lib.length) LIB = { v: VERSION, fv, lib };
  return lib;
}

/** registered ECH block types, in registration order */
export function echTypes(): string[] { return Object.keys(ECH); }

/** increments on every successful registration — a React dependency, nothing more */
export function echVersion(): number { return VERSION; }

/** test seam only: drop every registration (also un-registers from FULL_REGISTRY) */
export function __resetEch(): void {
  Object.keys(ECH).forEach((k) => { delete FULL_REGISTRY[k]; delete ECH[k]; });
  VERSION = 0;
  LIB = null;
}

/**
 * THE FULL DECLARED FIELD MAP — for FORMAT checks, not for caps (2026-08-25).
 *
 * `grFor()` returns `echLimits`, which is filtered to the fields a cap may be enforced against:
 * it drops image/url/number/tel/select outright and drops anything without a `max` or a
 * `lines`+`cpl` pair. That is right for clamping and wrong for `pattern`, which is meaningful
 * on exactly the types that filter removes — a URL's shape, a price's format, a phone number.
 *
 * Layne reported every pattern doing nothing; this is why. 68 of the 88 patterns on the live
 * blocks sit on url/number/tel, and the Inspector was handing `checkPattern` an `undefined`
 * rule for all of them, which passes unconditionally.
 */
export function patternRulesFor(type: string): Record<string, GuardrailField> {
  const m = ECH[type];
  return (m && m.declared) || {};
}
